import { useEffect, useState } from "preact/hooks";
import { Link, useLocation } from "wouter";
import { Invoices, type InvoiceDetail as InvoiceDetailT } from "../lib/api";
import { InvoicePreview } from "../components/InvoicePreview";
import { StatusBadge } from "../components/StatusBadge";
import { describeError } from "../lib/errors";
import { formatDateSk } from "@shared/format";
import { todayIso } from "@shared/date";

export function InvoiceDetail({ slug }: { slug: string }) {
  const [, setLocation] = useLocation();
  const [data, setData] = useState<InvoiceDetailT | null>(null);
  const [previewNumber, setPreviewNumber] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmExport, setConfirmExport] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Invoices.get(slug)
      .then(async (r) => {
        if (cancelled) return;
        setData(r);
        if (r.invoice.status === "draft") {
          const next = await Invoices.nextNumber(r.invoice.issue_date).catch(
            () => null
          );
          if (!cancelled && next) setPreviewNumber(next.number);
        } else {
          setPreviewNumber(null);
        }
      })
      .catch((e) => !cancelled && setError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const slugFor = (inv: { id: number; number: string | null }) =>
    inv.number ?? String(inv.id);

  async function markIssued() {
    if (!data || busy) return;
    setBusy("issue");
    try {
      const r = await Invoices.update(slug, { status: "issued" });
      // Server assigns the issued slot; redirect to the new slug.
      const newSlug = slugFor(r.invoice);
      if (newSlug !== slug) {
        setLocation(`/invoices/${newSlug}`);
        return;
      }
      const next = await Invoices.get(newSlug);
      setData(next);
      setPreviewNumber(null);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(null);
    }
  }

  async function duplicate() {
    if (!data || busy) return;
    setBusy("dup");
    try {
      const r = await Invoices.duplicate(slug);
      setLocation(`/invoices/${slugFor(r.invoice)}/edit`);
    } catch (e) {
      setError(describeError(e));
      setBusy(null);
    }
  }

  async function download() {
    if (!data || busy) return;
    setBusy("pdf");
    try {
      const { downloadInvoicePdf } = await import("../pdf/download");
      await downloadInvoicePdf(data);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(null);
    }
  }

  async function downloadXml() {
    if (!data || busy) return;
    setBusy("xml");
    try {
      const { downloadEinvoice } = await import("../pdf/download");
      await downloadEinvoice(data);
      if (!data.invoice.einvoice_exported_at) {
        setData(await Invoices.get(slug));
      }
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(null);
      setConfirmExport(false);
    }
  }

  async function remove() {
    if (!data || busy) return;
    setBusy("del");
    try {
      await Invoices.remove(slug);
      setLocation("/");
    } catch (e) {
      setError(describeError(e));
      setBusy(null);
      setConfirmDelete(false);
    }
  }

  if (error) {
    return (
      <main class="app-shell">
        <p class="error">{error}</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main class="app-shell">
        <p class="muted">Načítavam…</p>
      </main>
    );
  }
  const { invoice, items, customer, supplier } = data;
  const displayNumber =
    invoice.status === "issued"
      ? invoice.number ?? "—"
      : previewNumber ?? "—";
  const editSlug = slugFor(invoice);
  const locked = invoice.einvoice_exported_at !== null;
  return (
    <main class="app-shell">
      <header class="detail__head">
        <div>
          <h1>Faktúra {displayNumber}</h1>
          <StatusBadge status={invoice.status} />
          {locked && (
            <p class="muted">
              E-faktúra exportovaná{" "}
              {formatDateSk(todayIso(new Date(invoice.einvoice_exported_at!)))}{" "}
              — faktúra je uzamknutá.
            </p>
          )}
        </div>
        <div class="detail__actions">
          <button
            type="button"
            class="btn"
            onClick={download}
            disabled={!!busy}
          >
            {busy === "pdf" ? "…" : "Stiahnuť PDF"}
          </button>
          {invoice.status === "issued" && (
            <button
              type="button"
              class="btn"
              onClick={() => (locked ? downloadXml() : setConfirmExport(true))}
              disabled={!!busy}
            >
              {busy === "xml" ? "…" : "Stiahnuť e-faktúru (XML)"}
            </button>
          )}
          <button type="button" class="btn" onClick={duplicate} disabled={!!busy}>
            {busy === "dup" ? "…" : "Duplikovať"}
          </button>
          {!locked && (
            <Link href={`/invoices/${editSlug}/edit`} class="btn">
              Upraviť
            </Link>
          )}
          {invoice.status === "draft" && (
            <button
              type="button"
              class="btn primary"
              onClick={markIssued}
              disabled={!!busy}
            >
              {busy === "issue" ? "…" : "Vystaviť"}
            </button>
          )}
          {!locked && (
            <button
              type="button"
              class="btn btn--danger"
              onClick={() => setConfirmDelete(true)}
              disabled={!!busy}
              title={
                invoice.status === "issued"
                  ? "Pozor: mazanie vystavenej faktúry"
                  : undefined
              }
            >
              {busy === "del" ? "…" : "Zmazať"}
            </button>
          )}
        </div>
      </header>

      <InvoicePreview
        invoice={{
          ...invoice,
          number: invoice.status === "issued" ? invoice.number : previewNumber,
          variable_symbol:
            invoice.status === "issued"
              ? invoice.variable_symbol
              : previewNumber
                ? previewNumber.replace(/\D/g, "")
                : null,
        }}
        items={items}
        supplier={supplier}
        customer={customer}
      />

      {confirmExport && (
        <div class="modal" role="dialog" aria-modal="true">
          <div
            class="modal__backdrop"
            onClick={() => !busy && setConfirmExport(false)}
          />
          <div class="modal__card">
            <h2>Vytvoriť e-faktúru {invoice.number}?</h2>
            <p>
              E-faktúra (XML) sa uloží ako archívna kópia a faktúra sa
              uzamkne — už ju nebude možné upraviť ani zmazať. Prípadnú opravu
              po odoslaní treba riešiť dobropisom. Skontrolujte údaje pred
              pokračovaním.
            </p>
            <div class="modal__actions">
              <button
                type="button"
                class="btn"
                onClick={() => setConfirmExport(false)}
                disabled={!!busy}
              >
                Zrušiť
              </button>
              <button
                type="button"
                class="btn primary"
                onClick={downloadXml}
                disabled={!!busy}
                autoFocus
              >
                {busy === "xml" ? "Vytváram…" : "Vytvoriť a stiahnuť"}
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div class="modal" role="dialog" aria-modal="true">
          <div
            class="modal__backdrop"
            onClick={() => !busy && setConfirmDelete(false)}
          />
          <div class="modal__card">
            <h2>
              {invoice.status === "issued"
                ? `Zmazať vystavenú faktúru ${invoice.number}?`
                : "Zmazať tento koncept?"}
            </h2>
            {invoice.status === "issued" ? (
              <p>
                Táto faktúra už mohla byť odovzdaná odberateľovi a zaúčtovaná.
                Mazanie vystavených faktúr nie je v súlade s § 76 zákona o DPH
                — namiesto toho zvážte vystavenie opravného daňového dokladu
                (dobropisu).
              </p>
            ) : (
              <p>Operáciu nie je možné vrátiť späť.</p>
            )}
            <div class="modal__actions">
              <button
                type="button"
                class="btn"
                onClick={() => setConfirmDelete(false)}
                disabled={!!busy}
              >
                Zrušiť
              </button>
              <button
                type="button"
                class="btn btn--danger"
                onClick={remove}
                disabled={!!busy}
                autoFocus
              >
                {busy === "del" ? "Mažem…" : "Zmazať"}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
